import { useState, useEffect } from "react";
import { format, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Search, Calendar, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import type { CuratedJob, CuratedJobFormData } from "@/types/curatedJob.types";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useIsMobile } from "@/hooks/use-mobile";

export default function CuratedJobs() {
  usePageMeta({
    title: "Vagas Curadas - Data Hire Club",
    description: "Gerenciamento de vagas curadas",
    noindex: true,
  });

  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [jobs, setJobs] = useState<CuratedJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<CuratedJob | null>(null);
  const [formData, setFormData] = useState<CuratedJobFormData>({
    company_name: "",
    title: "",
    work_model: "",
    description: "",
    external_link: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  // Helper to format date and calculate days since posted
  const getDateInfo = (createdAt: string) => {
    const date = new Date(createdAt);
    const daysAgo = differenceInDays(new Date(), date);
    const formattedDate = format(date, "dd/MM/yyyy", { locale: ptBR });
    const isOld = daysAgo > 30;
    return { formattedDate, daysAgo, isOld };
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch = job.company_name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    
    const matchesStatus = 
      statusFilter === "all" || 
      job.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("curated_jobs")
        .select("*")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error("Erro ao buscar vagas:", error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as vagas",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (job?: CuratedJob) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        company_name: job.company_name,
        title: job.title,
        work_model: job.work_model || "",
        description: job.description,
        external_link: job.external_link,
      });
    } else {
      setEditingJob(null);
      setFormData({
        company_name: "",
        title: "",
        work_model: "",
        description: "",
        external_link: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingJob(null);
    setFormData({
      company_name: "",
      title: "",
      work_model: "",
      description: "",
      external_link: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingJob) {
        const { error } = await supabase
          .from("curated_jobs")
          .update(formData)
          .eq("id", editingJob.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Vaga atualizada com sucesso",
        });
      } else {
        const { error } = await supabase.from("curated_jobs").insert([formData]);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Vaga criada com sucesso",
        });
      }

      handleCloseDialog();
      fetchJobs();
    } catch (error) {
      console.error("Erro ao salvar vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar a vaga",
        variant: "destructive",
      });
    }
  };

  const handleToggleStatus = async (job: CuratedJob) => {
    try {
      const newStatus = job.status === "active" ? "inactive" : "active";
      const { error } = await supabase
        .from("curated_jobs")
        .update({ status: newStatus })
        .eq("id", job.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: `Vaga ${newStatus === "active" ? "ativada" : "desativada"} com sucesso`,
      });

      fetchJobs();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      toast({
        title: "Erro",
        description: "Não foi possível alterar o status da vaga",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja deletar esta vaga?")) return;

    try {
      const { error } = await supabase
        .from("curated_jobs")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Vaga deletada com sucesso",
      });

      fetchJobs();
    } catch (error) {
      console.error("Erro ao deletar vaga:", error);
      toast({
        title: "Erro",
        description: "Não foi possível deletar a vaga",
        variant: "destructive",
      });
    }
  };

  return (
    <CandidateLayout maxWidth="7xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Vagas de Curadoria</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Gerencie vagas curadas manualmente
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Vaga
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por empresa..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(value: "all" | "active" | "inactive") => setStatusFilter(value)}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="active">Ativas</SelectItem>
            <SelectItem value="inactive">Inativas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p>Carregando...</p>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg border">
          <p className="text-muted-foreground">Nenhuma vaga curada encontrada</p>
          <Button className="mt-4" onClick={() => handleOpenDialog()}>
            Criar primeira vaga
          </Button>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg border">
          <p className="text-muted-foreground">Nenhuma vaga encontrada para sua busca</p>
          <Button 
            variant="outline" 
            className="mt-4" 
            onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}
          >
            Limpar filtros
          </Button>
        </div>
      ) : isMobile ? (
        // Mobile: Cards view
        <div className="space-y-3">
          {filteredJobs.map((job) => {
            const { formattedDate, daysAgo, isOld } = getDateInfo(job.created_at);
            return (
            <Card key={job.id} className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{job.company_name}</p>
                  <p className="text-sm text-muted-foreground truncate">{job.title}</p>
                  <div className={`flex items-center gap-1 mt-1 text-xs ${isOld ? "text-yellow-600 dark:text-yellow-500" : "text-muted-foreground"}`}>
                    <Calendar className="h-3 w-3" />
                    <span>{formattedDate}</span>
                    <span>({daysAgo} dias)</span>
                    {isOld && <AlertTriangle className="h-3 w-3" />}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <Switch
                    checked={job.status === "active"}
                    onCheckedChange={() => handleToggleStatus(job)}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-xs">
                    {job.work_model || "N/A"}
                  </Badge>
                  <Badge variant={job.status === "active" ? "default" : "secondary"} className="text-xs">
                    {job.status === "active" ? "Ativa" : "Inativa"}
                  </Badge>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(job)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(job.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          );
          })}
        </div>
      ) : (
        // Desktop: Table view
        <div className="bg-card rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Vaga</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead>Publicada em</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.map((job) => {
                const { formattedDate, daysAgo, isOld } = getDateInfo(job.created_at);
                return (
                <TableRow key={job.id}>
                  <TableCell className="font-medium">{job.company_name}</TableCell>
                  <TableCell>{job.title}</TableCell>
                  <TableCell>{job.work_model || "Não especificado"}</TableCell>
                  <TableCell>
                    <div className={`flex items-center gap-1 ${isOld ? "text-yellow-600 dark:text-yellow-500" : ""}`}>
                      <span>{formattedDate}</span>
                      <span className="text-xs text-muted-foreground">({daysAgo}d)</span>
                      {isOld && <AlertTriangle className="h-4 w-4" />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={job.status === "active"}
                        onCheckedChange={() => handleToggleStatus(job)}
                      />
                      <Badge variant={job.status === "active" ? "default" : "secondary"}>
                        {job.status === "active" ? "Ativa" : "Inativa"}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(job)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(job.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>
              {editingJob ? "Editar Vaga Curada" : "Adicionar Vaga Curada"}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da vaga que será exibida na plataforma
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Nome da Empresa *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) =>
                  setFormData({ ...formData, company_name: e.target.value })
                }
                placeholder="Ex: Google, Microsoft..."
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Título da Vaga *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="Ex: Analista de Dados Sênior"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="work_model">Modelo de Trabalho</Label>
              <Select
                value={formData.work_model}
                onValueChange={(value) =>
                  setFormData({ ...formData, work_model: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o modelo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Remoto">Remoto</SelectItem>
                  <SelectItem value="Presencial">Presencial</SelectItem>
                  <SelectItem value="Híbrido">Híbrido</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição da Vaga *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Descreva a vaga, requisitos, responsabilidades..."
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="external_link">Link Externo *</Label>
              <Input
                id="external_link"
                type="url"
                value={formData.external_link}
                onChange={(e) =>
                  setFormData({ ...formData, external_link: e.target.value })
                }
                placeholder="https://linkedin.com/jobs/..."
                required
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseDialog}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingJob ? "Atualizar Vaga" : "Publicar Vaga"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </CandidateLayout>
  );
}
